import React from 'react';

import {
  Button,
  ComponentPropertiesIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tag,
  Text,
  Tooltip,
  VStack,
  HStack,
  View,
  UndoIcon,
} from '@packages/systems/spring';
import {OSX} from '@packages/utilities/environment';
import PseudoProvider from '@packages/systems/design-system/PseudoProvider';
import {PropertyLabelBase} from '@packages/systems/ui/shared-ui/PropertyLabel/PropertyLabel';
import {
  type PropertyOrigin,
  type PropertyOriginRule,
} from '@packages/systems/style-legacy/values';
import {BREAKPOINT_ID_MAIN} from '@packages/systems/style/breakpoint-ids';
import {BreakpointID} from '@packages/systems/style/types';
import BreakpointsIcons from '@packages/systems/ui/shared-ui/BreakpointsIcons';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  findInheritanceSource,
  getOverrides,
} from '@packages/systems/component-canvas/designer';

const inheritedOriginRule: PropertyOriginRule = {
  tokens: [],
  mediaQueryKey: BREAKPOINT_ID_MAIN,
};

const inheritedOrigin: PropertyOrigin = {
  type: 'inherited',
  rule: inheritedOriginRule,
};

const overriddenOrigin: PropertyOrigin = {
  type: 'local',
  rule: inheritedOriginRule,
};

const ResetPopoverContent: React.FC<{
  label: string;
  onReset: () => void;
}> = ({label, onReset}) => {
  return (
    <PopoverContent
      aria-label={`${label} reset`}
      data-automation-id={`${label}-reset`}
      shouldAnimate={false}
      isSeamless
    >
      <View paddingY="$2">
        <Button onClick={onReset} variant="ghost" prefix={UndoIcon}>
          <HStack>
            <Text>Reset</Text>
            <Text colorVariant="secondary">
              {OSX ? 'Option + click' : 'Alt + click'} to reset
            </Text>
          </HStack>
        </Button>
      </View>
    </PopoverContent>
  );
};

const InheritancePopoverContent: React.FC<{
  label: string;
  inheritedFrom: React.ReactNode;
}> = ({label, inheritedFrom}) => {
  return (
    <PopoverContent
      aria-label={`${label} inheritance details`}
      data-automation-id={`${label}-inheritance-details`}
      shouldAnimate={false}
    >
      <VStack>
        <Text colorVariant="secondary" font="body">
          Value comes from:
        </Text>
        <Text font="body" weight="medium">
          {inheritedFrom}
        </Text>
      </VStack>
    </PopoverContent>
  );
};

const InteractiveLabel: React.FC<{
  origin: PropertyOrigin;
  label: string;
  automationId: string;
  tooltip?: React.ReactNode;
  onTriggerClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onTriggerKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  popoverContent: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean, event?: Event, reason?: string) => void;
}> = ({
  origin,
  label,
  automationId,
  tooltip,
  onTriggerClick,
  onTriggerKeyDown,
  popoverContent,
  isOpen,
  onOpenChange,
}) => {
  return (
    <View flex={1}>
      <Popover
        placement="bottom-start"
        maxWidth={240}
        shouldTrapFocus={false}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
      >
        <PseudoProvider>
          {({hover}) => (
            <PopoverTrigger asChild>
              <View
                display="inline-flex"
                alignItems="center"
                cursor="pointer"
                gap={0}
                marginLeft="-$0.5"
                paddingLeft="$0.5"
                onClick={onTriggerClick}
                onKeyDown={onTriggerKeyDown}
                data-origin={origin.type}
                data-automation-id={automationId}
              >
                <PropertyLabelBase
                  hover={hover}
                  origin={origin}
                  tooltip={tooltip}
                  style={{cursor: 'pointer'}}
                >
                  {label}
                </PropertyLabelBase>
              </View>
            </PopoverTrigger>
          )}
        </PseudoProvider>
        {popoverContent}
      </Popover>
    </View>
  );
};

/**
 * This value is used to enable truncation of long variant names
 * in the Tag that renders within the frame setting inheritance popover.
 */
const VARIANT_TAG_MAX_WIDTH = 185;

/**
 * Determines what breakpoint/variant a frame setting is inherited from.
 * Returns a React node showing the source breakpoint icon and variant name,
 * or null if the setting is not inherited (i.e., has a local value at this breakpoint).
 *
 * Inheritance rules:
 * - Non-base frames inherit from the nearest same-variant override in the cascade
 * - Non-base frames otherwise inherit from the base frame (same or cascading breakpoint)
 * - Base frames inherit from cascading breakpoints within the same breakpoint group
 * - When no explicit override source is found, values come from base@main by default
 */
export const findInheritedFrom =
  (
    currentFrame: ExpressionElement,
    currentName: string,
    baseFrame: ExpressionElement,
    baseName: string,
    mediaQuery: BreakpointID
  ) =>
  (setting: 'width' | 'height' | 'color'): React.ReactNode => {
    const variantOverrides = getOverrides(currentFrame);
    const variantInheritFrom = variantOverrides
      ? findInheritanceSource(variantOverrides, mediaQuery, setting, false)
      : undefined;

    const isBaseFrame = currentFrame.id === baseFrame.id;

    const baseInheritFrom =
      variantInheritFrom || isBaseFrame
        ? undefined
        : findInheritanceSource(
            getOverrides(baseFrame),
            mediaQuery,
            setting,
            true
          );

    const mq =
      variantInheritFrom ||
      baseInheritFrom ||
      (isBaseFrame && mediaQuery === BREAKPOINT_ID_MAIN
        ? undefined
        : BREAKPOINT_ID_MAIN);

    if (!mq) return null;

    const frameName = variantInheritFrom ? currentName : baseName;
    const BreakpointIcon = BreakpointsIcons[mq];

    return (
      <HStack>
        <BreakpointIcon size="small" />
        <Tag
          variant="solid"
          density="compact"
          colorVariant="green"
          prefix={ComponentPropertiesIcon}
          maxWidth={VARIANT_TAG_MAX_WIDTH}
        >
          {frameName}
        </Tag>
      </HStack>
    );
  };

export const FrameFieldLabel: React.FC<{
  label: string;
  inheritedFrom: React.ReactNode;
  automationId: string;
  onReset?: () => void;
  /**
   * Optional hover description (Style panel / PropertyLabel pattern).
   * Stays the same whether the value is default or custom — reset copy
   * lives in the click menu, not here.
   */
  tooltip?: React.ReactNode;
}> = ({label, inheritedFrom, automationId, onReset, tooltip}) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const showResetButton = Boolean(onReset);
  const showInheritance = inheritedFrom && !showResetButton;

  const handleTriggerClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (showResetButton && e.altKey && onReset) {
        e.preventDefault();
        e.stopPropagation();
        onReset();
        setIsPopoverOpen(false);
        return;
      }

      setIsPopoverOpen((prev) => !prev);
    },
    [onReset, showResetButton]
  );

  const handlePopoverOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setIsPopoverOpen(nextOpen);
    },
    [setIsPopoverOpen]
  );

  const handleReset = React.useCallback(() => {
    onReset?.();
    setIsPopoverOpen(false);
  }, [onReset]);

  if (!showResetButton && !showInheritance) {
    const defaultLabel = (
      <Text
        font="body"
        colorVariant="secondary"
        data-automation-id={automationId}
        data-origin="default"
      >
        {label}
      </Text>
    );
    return (
      <View flex={1}>
        {tooltip ? (
          <Tooltip label={tooltip} placement="top" asChild>
            {defaultLabel}
          </Tooltip>
        ) : (
          defaultLabel
        )}
      </View>
    );
  }

  return (
    <InteractiveLabel
      origin={showResetButton ? overriddenOrigin : inheritedOrigin}
      label={label}
      automationId={automationId}
      tooltip={tooltip}
      isOpen={isPopoverOpen}
      onOpenChange={handlePopoverOpenChange}
      onTriggerClick={handleTriggerClick}
      popoverContent={
        showResetButton ? (
          <ResetPopoverContent label={label} onReset={handleReset} />
        ) : (
          <InheritancePopoverContent
            label={label}
            inheritedFrom={inheritedFrom}
          />
        )
      }
    />
  );
};
